export const performance_management_setup_tutorial_config = {
    pageTitle: 'Performance Management Setup',
    page_id: 'performance_management_setup',
    autoStartWelcome: true,
    showFloatingButton: true,
    storagePrefix: 'perf_mgmt_setup',
    
    // Mock data creators
    mockDataCreators: {
        performanceCycles: function() {
            return [
                {
                    cycle_name: 'Annual Review 2024',
                    company_name: 'Main Company',
                    department_name: 'Engineering',
                    from_date: '2024-01-01',
                    to_date: '2024-12-31'
                },
                {
                    cycle_name: 'Q1 2024 Review',
                    company_name: 'Main Company',
                    department_name: 'Sales',
                    from_date: '2024-01-01',
                    to_date: '2024-03-31'
                }
            ];
        },
        
        companyObjectives: function() {
            return [
                {
                    objective_number: 1,
                    mission: 'Increase company revenue by 25% year-over-year',
                    edit: ko.observable(false),
                    goals: [
                        {
                            goal_nr: '1.1',
                            goal: 'Expand into 3 new markets',
                            edit: ko.observable(false),
                            subgoals: [
                                {
                                    subgoal_nr: '1.1.1',
                                    subgoal: 'Complete market research',
                                    edit: ko.observable(false)
                                }
                            ]
                        }
                    ]
                }
            ];
        },
        
        ratingMethods: function() {
            return [
                {
                    rating_method_name: '5-Point Scale',
                    rating_method_description: 'Standard 1-5 rating scale',
                    rating_method_type: 'single-select',
                    active: true,
                    editing: ko.observable(false),
                    options: [
                        { option_symbol: '5', option_name: 'Exceptional' },
                        { option_symbol: '4', option_name: 'Exceeds Expectations' },
                        { option_symbol: '3', option_name: 'Meets Expectations' },
                        { option_symbol: '2', option_name: 'Needs Improvement' },
                        { option_symbol: '1', option_name: 'Unsatisfactory' }
                    ]
                }
            ];
        }
    },
    
    // Mock data cleaners
    mockDataCleaners: {
        performanceCycles: function() {
            if (this.performance_cycles && ko.isObservable(this.performance_cycles)) {
                this.performance_cycles.removeAll();
            }
        },
        
        companyObjectives: function() {
            if (this.company_objectives && ko.isObservable(this.company_objectives)) {
                this.company_objectives.removeAll();
            }
        },
        
        ratingMethods: function() {
            if (this.rating_methods && ko.isObservable(this.rating_methods)) {
                this.rating_methods.removeAll();
            }
        }
    },
    step_to_tab_mapping: {
        // Overview steps (no tab switch needed)
        0: null,  // Welcome step
        1: null,  // Tab navigation explanation
        
        // Performance Cycles
        2: 'CycleSetup',
        3: 'CycleSetup',
        4: 'CycleSetup',
        5: 'CycleSetup',
        
        // Company Objectives
        //5: 'CompanyObjectives',
        6: 'CompanyObjectives',
        7: 'CompanyObjectives',
        8: 'CompanyObjectives',
        
        // Cycle Stages
        9: 'StatusSetup',
        10: 'StatusSetup',
        11: 'StatusSetup',

        //Rating Methods
        12: 'RatingSetup',
        13: 'RatingSetup',
        14: 'RatingSetup',
        15: 'RatingSetup',
        16: 'RatingSetup',
    
        
        // Objective Measures
        17: 'MeasureSetup',
        18: 'MeasureSetup',
        
        // Peer Groups
        19: 'PeerGroup',
        20: 'PeerGroup',
        21: 'PeerGroup',
        
        // Access Control
        22: 'AccessControl',
        23: 'AccessControl',
        24: 'AccessControl',
        
        // Wrap-up steps (no tab switch)
        25: null,  // Common features
        26: null,  // Best practices
        27: null,  // Deletion warning
        28: null   // Summary
    },
    
    // Tutorial step definitions
    steps: [
        // OVERVIEW
        {
            element: ['.ps-container', '.subheading h4.ps-left-header'],
            title: 'Performance Management Setup',
            description: 'This is your central hub for configuring all aspects of performance management. Here, you’ll set up cycles, objectives, rating methods, and more.',
            requirements: 'Performance Management Administrator',
            additionalInfo: `
                <p><strong>What you can configure:</strong></p>
                <ul>
                    <li>Performance Cycles (review periods)</li>
                    <li>Company Objectives (strategic goals)</li>
                    <li>Cycle Stages (workflow steps)</li>
                    <li>Rating Methods (evaluation scales)</li>
                    <li>Objective Measures (KPIs)</li>
                    <li>Peer Groups (360° feedback groups)</li>
                    <li>Access Controls (permissions)</li>
                </ul>
                <p><small>Tip: Complete setup in the order shown above for the best results</small></p>
            `,
            position: 'bottom',
            optional: false
        },

        // TAB NAVIGATION
        {
            element: ['.ps-tabs', 'ul.ps-tabs', '[data-tutorial-element="tab_navigation"]'],
            title: 'Navigation Tabs',
            description: 'Use the navigation tabs to move between different configuration sections. Each tab represents a key area of performance management setup.',
            additionalInfo: `
            `,
            position: 'left',
            optional: false
        },

        // PERFORMANCE CYCLES TAB
        {
            element: ['li[data-tabname="CycleSetup"]', 'a[href*="#"]:has(span:contains("Performance Cycles"))', '[data-tutorial-element="performance_cycle_table"]'],
            title: 'Performance Cycles',
            description: 'Define when performance reviews take place. Create annual, semi-annual, quarterly, or monthly review cycles.',
            purpose: 'Establish timeframes for performance reviews.',
            additionalInfo: `
                <p><strong>Common Cycle Types:</strong></p>
                <ul>
                    <li><strong>Annual:</strong> Comprehensive yearly review</li>
                    <li><strong>Semi-Annual:</strong>Two reviews per year</li>
                    <li><strong>Quarterly:</strong>Four reviews per year</li>
                    <li><strong>Monthly:</strong> Continuous feedback approach</li>
                </ul>
                <p><strong>What to configure:</strong></p>
                <ul>
                    <li>Cycle name and description</li>
                    <li>Start and end dates</li>
                    <li>Company or Department scope</li>
                    <li>Linked objective plans</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: ['button:has(span:contains("Add cycle"))', '.ps-btn-primary:has(span:contains("Add cycle"))', '[data-tutorial-element="add_performance_cycle"]'],
            title: 'Add New Cycle',
            description: 'Create a new performance cycle by defining its timeframe and scope.',
            additionalInfo: `
                <p><strong>Steps to create a cycle:</strong></p>
                <ol>
                    <li>Click "Add cycle" button</li>
                    <li>Enter cycle name</li>
                    <li>Set start and end dates</li>
                    <li>Select company or department</li>
                    <li>Save the cycle</li>
                </ol>
            `,
            position: 'left',
            optional: false
        },

        {
            element: ['table.ps-table-no-border.performance-manage-table.table-selectable-fields','[data-tutorial-element="performance_cycle_table"]'],
            title: 'Cycle List',
            description: 'View all configured performance cycles. Click any row to see linked objective plans and details.',
            additionalInfo: `
                <p><strong>Table columns:</strong></p>
                <ul>
                    <li>Performance cycle name</li>
                    <li>Company/Department</li>
                    <li>Start date</li>
                    <li>End date</li>
                    <li>Actions (edit/delete)</li>
                </ul>
                <p><small>Tip: Click column headers to sort.</small></p>
            `,
            position: 'left',
            optional: false,
            onafterchange: function(element) {
                const helperNumber = document.querySelector('.introjs-helperNumberLayer');
                if (helperNumber) {
                    helperNumber.style.top = '0';
                    helperNumber.style.left = '0';
                }
            }
        },

        // COMPANY OBJECTIVES TAB
        {
            element: ['li[data-tabname="CompanyObjectives"]'],
            title: 'Company Objectives',
            description: 'Define your organisation’s strategic goals, which can cascade down to individual employee objectives.',
            purpose: 'Align individual goals with company strategy.',
            additionalInfo: `
                <p><strong>Hierarchy Structure:</strong></p>
                <ul>
                    <li><strong>Objectives:</strong> High-level organisational missions</li>
                    <li><strong>Goals:</strong> Major initiatives under each objective</li>
                    <li><strong>Subgoals:</strong> Specific tasks under each goal</li>
                </ul>
                <p><strong>Benefits:</strong></p>
                <ul>
                    <li>Creates strategic alignment</li>
                    <li>Enables goal cascading</li>
                    <li>Improves visibility</li>
                    <li>Tracks company progress</li>
                </ul>
                <p><small>(Optional feature – use only if needed.)</small></p>
            `,
            position: 'bottom',
            optional: true
        },

        {
            element: ['button:has(span:contains("Add objective"))', '.ps-btn-primary:has(.fa-plus):has(span:contains("Add objective"))', '[data-tutorial-element="add_performance_objective"]'],
            title: 'Add Company Objective',
            description: 'Create a new strategic objective for your organization. You can then add goals and subgoals under it.',
            additionalInfo: `
                <p><strong>Example structure:</strong></p>
                <ul>
                    <li><strong>Objective:</strong> Increase revenue by 25%</li>
                    <li><strong>Goal:</strong> Expand into 3 new markets</li>
                    <li>    <strong>Subgoal:</strong> Complete market research</li>
                    <li>    <strong>Subgoal:</strong> Hire regional managers</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },

        {
            element: ['.ps-switch:has(input[data-bind*="edit_all"])', 'div:has(span:contains("Edit"))','[data-tutorial-element="objective_buttons"]'],
            title: 'Edit Mode Toggle',
            description: 'Switch to edit mode to modify objectives, add goals, or delete items. Turn off edit mode for view-only access.',
            additionalInfo: `
                <p><strong>Edit mode enables:</strong></p>
                <ul>
                    <li>Editing objective text</li>
                    <li>Adding goals and subgoals</li>
                    <li>Deleting items</li>
                    <li>Reordering subgoals</li>
                </ul>
            `,
            position: 'left',
            optional: true
        },

        // CYCLE STAGES TAB
        {
            element: ['li[data-tabname="StatusSetup"]' , '[data-tutorial-element="stages_tab"]'],
            title: 'Cycle Stages',
            description: 'Configure the workflow stages employees progress through during each performance cycle.',
            purpose: 'Define the performance review process.',
            additionalInfo: `
                <p><strong>Typical stage sequence:</strong></p>
                <ol>
                    <li>Goal Setting</li>
                    <li>Mid-Year Check-in</li>
                    <li>Self-Assessment</li>
                    <li>Peer Review (optional)</li>
                    <li>Manager Review</li>
                    <li>Discussion/Feedback</li>
                    <li>Final Rating</li>
                </ol>
                <p><strong>Data Lock Rules:</strong></p>
                <ul>
                    <li>Control what can be edited at each stage</li>
                    <li>Prevent changes after certain points</li>
                    <li>Ensure data integrity</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: ['select[data-bind*="performance_cycles_select"]', '.select-container select.ps-input'],
            title: 'Select Performance Cycle',
            description: 'Choose the cycle you want to configure stages for. Each cycle can have its own workflow.',
            additionalInfo: `
                <p><strong>Why per-cycle stages?</strong></p>
                <ul>
                    <li>Different cycles may require unique workflows</li>
                    <li>Annual reviews tend to be more detailed</li>
                    <li>Quarterly reviews may be simpler</li>
                </ul>
            `,
            position: 'right',
            optional: false
        },
        /* needs refinements
        {
            element: [ '[data-tutorial-element="add_stage"]'],
            title: 'Add New Stage',
            description: 'Add a new step to your performance cycle workflow. Define what happens at each stage.',
            additionalInfo: `
                <p><strong>For each stage, define:</strong></p>
                <ul>
                    <li>Stage name</li>
                    <li>Sequence order</li>
                    <li>Data lock rules</li>
                    <li>Required actions</li>
                </ul>
            `,
            position: 'center',
            optional: false
        },

        {
            element: ['[data-tutorial-element="replace_steps"]'],
            title: 'Replace with Template',
            description: 'Quickly apply a predefined stage template instead of creating stages one by one.',
            additionalInfo: `
                <p><strong>Templates available:</strong></p>
                <ul>
                    <li>Standard annual review</li>
                    <li>Simple quarterly review</li>
                    <li>360-degree feedback cycle</li>
                    <li>Continuous feedback model</li>
                </ul>
                <p><small>This will replace ALL existing stages</small></p>
            `,
            position: 'center',
            optional: false
        },
        */
        {
            element: ['button:has(span:contains("Default stages"))', '.ps-btn-primary:has(span:contains("Default stages"))', '[data-tutorial-element="default_steps"]'],
            title: 'Default Stages',
            description: 'View and configure the default stage template that applies to new cycles.',
            additionalInfo: `
                <p><strong>Default stages:</strong></p>
                <ul>
                    <li>Applied automatically to new cycles</li>
                    <li>Can be customised per cycle</li>
                    <li>Saves setup time</li>
                </ul>
            `,
            position: 'top',
            optional: false
        },

        // RATING METHODS TAB
        {
            element: ['li[data-tabname="RatingSetup"]' , '[data-tutorial-element="rating_tab"]'],
            title: 'Rating Methods',
            description: 'Define how performance will be evaluated across your organisation.',
            purpose: 'Standardise performance evaluations.',
            additionalInfo: `
                <p><strong>Rating method types:</strong></p>
                <ul>
                    <li><strong>Single-select:</strong> Choose one option (e.g., 1–5 scale))</li>
                    <li><strong>Multi-select:</strong> Choose multiple competencies</li>
                    <li><strong>Text input:</strong>Open-ended feedback</li>
                    <li><strong>Range/Percentage:</strong> Slider-based rating</li>
                    <li><strong>Numeric value:</strong> Enter specific numbers</li>
                </ul>
                <p><strong>Best practices:</strong></p>
                <ul>
                    <li>Use consistent scales across all cycles</li>
                    <li>Clearly define rating criteria</li>
                    <li>Train evaluators on using the methods</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: ['li[data-tabname="single-select"]', '[data-tutorial-element="rating_categories"]'],
            title: 'Rating Type Categories',
            description: 'Filter rating methods by type. Each category displays its specific input format.',
            additionalInfo: `
                <p><strong>When to use each type:</strong></p>
                <ul>
                    <li><strong>Single-select:</strong> Overall ratings</li>
                    <li><strong>Multi-select:</strong> Skills or competency assessments</li>
                    <li><strong>Text input:</strong> Detailed comments</li>
                    <li><strong>Range:</strong> Achievement percentages</li>
                    <li><strong>Numeric:</strong> KPI-based scoring</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: ['button:has(span:contains("Add rating method"))',  '[data-tutorial-element="add_rating_method"]'],
            title: 'Add Rating Method',
            description: 'Create a new rating method. You\'ll choose the type and define the options/scale.',
            additionalInfo: `
                <p><strong>Example methods:</strong></p>
                <ul>
                    <li>5-point scale (1-5)</li>
                    <li>Letter grades (A-F)</li>
                    <li>Exceeds/Meets/Below</li>
                    <li>Percentage achievement</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },

        {
            element: ['table.performance-manage-table th:contains("Use?")', '[data-tutorial-element="active_status"]'],
            title: 'Active Status',
            description: 'Toggle whether a rating method is available for use. Inactive methods are hidden but retained for record-keeping.',
            additionalInfo: `
                <p><strong>Why deactivate?</strong></p>
                <ul>
                    <li>Testing new methods</li>
                    <li>Phasing out outdated scales</li>
                    <li>Maintaining historical data</li>
                    <li>Supporting varied cycle needs</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },

        {
            element: ['th:contains("Linked objectives")', 'td[data-bind*="total_linked_objective_plans"]'],
            title: 'Linked Objectives',
            description: 'View how many objective plans are using a specific rating method.',
            additionalInfo: `
                <p><strong>Why it matters:</strong></p>
                <ul>
                    <li>Active methods cannot be deleted</li>
                    <li>Helps track method popularity</li>
                    <li>Ensures consistency and traceability</li>
                </ul>
            `,
            position: 'left',
            optional: false
        },
        /* needs refinements
        {
            element: ['[data-tutorial-element="add_rating_option"]'],
            title: 'Add Rating Options',
            description: 'Add individual rating options to your selected method. Define the symbol/score and description.',
            additionalInfo: `
                <p><strong>Example for 5-point scale:</strong></p>
                <ul>
                    <li>Symbol: 5 | Option: Exceptional</li>
                    <li>Symbol: 4 | Option: Exceeds</li>
                    <li>Symbol: 3 | Option: Meets</li>
                    <li>Symbol: 2 | Option: Needs Improvement</li>
                    <li>Symbol: 1 | Option: Unsatisfactory</li>
                </ul>
                <p><small>Preview updates as you add options</small></p>
            `,
            position: 'top',
            optional: false
        },
        */
        // OBJECTIVE MEASURES TAB
        {
            element: ['li[data-tabname="MeasureSetup"]', '[data-tutorial-element="measure_tab"]'],
            title: 'Objective Measures',
            description: 'Define key performance indicators (KPIs) and metrics used to measure objective achievement.',
            purpose: 'Standardise measurement criteria',
            additionalInfo: `
                <p><strong>Common measures:</strong></p>
                <ul>
                    <li> Revenue or sales targets</li>
                    <li> Project completion rate</li>
                    <li> Customer satisfaction score</li>
                    <li>On-time delivery rate</li>
                    <li>Quality or defect metrics</li>
                    <li>Cost reduction goals</li>
                </ul>
                <p><strong>Benefits:</strong></p>
                <ul>
                    <li>Consistent evaluation across teams</li>
                    <li>Objective measurement clarity</li>
                    <li>Simplified performance analysis</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: [ '[data-tutorial-element="add_measure"]'],
            title: 'Add New Measure',
            description: 'Add new measure options to the Objective Measures list. These measures help define how performance objectives are tracked and evaluated.',
            additionalInfo: `
            `,
            position: 'right',
            optional: false
        },

        // PEER GROUPS TAB
        {
            element: ['li[data-tabname="PeerGroup"]'],
            title: 'Peer Groups',
            description: 'Set up groups for 360° feedback, defining who can provide peer reviews for whom.',
            purpose: 'Enable multi-rater performance feedback.',
            additionalInfo: `
                <p><strong>Peer group benefits:</strong></p>
                <ul>
                    <li>360° holistic feedback</li>
                    <li>Multiple perspectives</li>
                    <li>Identification of blind spots</li>
                    <li>More balanced evaluations</li>
                </ul>
                <p><strong>Setup considerations:</strong></p>
                <ul>
                    <li>Group employees who collaborate closely</li>
                    <li>Include cross-functional peers</li>
                    <li>Decide on anonymity settings</li>
                    <li>Define minimum reviewer numbers</li>
                </ul>
                <p><small>(Optional feature – use only if applicable.)</small></p>
            `,
            position: 'bottom',
            optional: true
        },

        {
            element: ['.custom-select-container', 'div[data-bind*="unique_peer_groups"]'],
            title: 'Peer Group List',
            description: 'View and manage peer groups. Click a group to view or edit its members.',
            additionalInfo: `
                <p><strong>Actions available:</strong></p>
                <ul>
                    <li>Edit group name</li>
                    <li>Add members</li>
                    <li>Delete group</li>
                    <li>View members</li>
                </ul>
            `,
            position: 'right',
            optional: true
        },

        {
            element: ['[data-tutorial-element="add_peer_group"]'],
            title: 'Add Peer Group',
            description: 'Create a new peer review group.',
            additionalInfo: `
                <p><strong>Creating a group:</strong></p>
                <ol>
                    <li>Click "Add peer group"</li>
                    <li>Enter group name</li>
                    <li>Select company or department filters</li>
                    <li>Add members</li>
                    <li>Save the group</li>
                </ol>
                <p><strong>Member considerations:</strong></p>
                <ul>
                    <li>Choose peers who work closely together</li>
                    <li>Maintain a balanced group size (3–7 members recommended)</li>
                    <li>Consider team dynamics</li>
                </ul>
            `,
            position: 'top',
            optional: true
        },

        // ACCESS CONTROL TAB
        {
            element: ['li[data-tabname="AccessControl"]'],
            title: 'Access Controls',
            description: 'Manage who can view, edit, and configure performance management features',
            requirements: 'System Administrator',
            purpose: 'Protect sensitive performance data and ensure proper access control.',
            additionalInfo: `
                <p><strong>Why it matters:</strong></p>
                <ul>
                    <li>Safeguards confidential reviews</li>
                    <li>Manages visibility of ratings</li>
                    <li>Controls edit permissions</li>
                    <li>Manage report access</li>
                    <li>Ensures compliance and audit readiness</li>
                </ul>
                <p><strong>Common role permissions:</strong></p>
                <ul>
                    <li><strong>Employees:</strong> View own data and complete self-reviews</li>
                    <li><strong>Managers:</strong> Review and rate direct reports</li>
                    <li><strong>Admins:</strong> Configure system and access settings</li>
                </ul>
            `,
            position: 'bottom',
            optional: false
        },

        {
            element: ['table.performance-manage-table th:contains("Feature")', 'tbody[data-bind*="feature_roles"]', '[data-tutorial-element="feature_table"]'],
            title: 'Feature Access List',
            description: 'Each row represents a feature or action. Configure which user roles can access each one.',
            requirements: 'System Administrator',
            additionalInfo: `
                <p><strong>Column details:</strong></p>
                <ul>
                    <li><strong>Feature:</strong> Name of the function</li>
                    <li><strong>Description:</strong> What it does</li>
                    <li><strong>Category:</strong> Feature grouping</li>
                    <li><strong>Roles:</strong> Who has access</li>
                </ul>
                <p><small>Click edit to modify role assignments</small></p>
            `,
            position: 'top',
            optional: false
        },

        {
            element: ['[data-tutorial-element="edit_feature"]'],
            title: 'Edit Role Access',
            description: 'Modify role permissions for each feature using checkboxes.Click edit to assign or remove roles for a specific feature. Use checkboxes to select applicable roles.',
            requirements: 'System Administrator',
            additionalInfo: `
                <p><strong>Editing access:</strong></p>
                <ol>
                    <li>Click Edit next to the feature.</li>
                    <li>Select or deselect role access.</li>
                    <li>Click Save to apply changes</li>
                    <li>Click Cancel to discard edits.</li>
                </ol>
                <p><small>⚠️ Be cautious — incorrect permissions can disrupt workflows.</small></p>
            `,
            position: 'left',
            optional: false
        },
        {
            element: ['.page_help', '.fa-info-circle'],
            title: 'Contextual Help',
            description: 'Click the information icon on any section to access detailed help.',
            requirements: 'Available to all userse',
            additionalInfo: `
                <p><strong>Help covers:</strong></p>
                <ul>
                    <li>Major sections and configuration areas</li>
                    <li>Complex setup features</li>
                    <li>Step-by-step guidance</li>
                </ul>
                <p><small>Tip: Always check contextual help if unsure.</small></p>
            `,
            position: 'left',
            optional: false
        },

        {
            element: ['[data-tutorial-element="performance_dashboard"]'],
            title: 'Performance Dashboard',
            description: 'Return to the main performance management dashboard.',
            position: 'left',
            optional: false
        },

        // BEST PRACTICES & TIPS
        {
            element: ['.ps-container'],
            title: 'Setup Best Practices',
            description: 'Key recommendations for successful setup and smooth implementation.',
            additionalInfo: `
                <p><strong>Recommended Setup Order:</strong></p>
                <ol>
                    <li>Create Performance Cycles</li>
                    <li>Define Rating Methods</li>
                    <li>Set up Objective Measures</li>
                    <li>Configure Cycle Stages</li>
                    <li>Add Company Objectives (optional)</li>
                    <li>Set up Peer Groups (if used)</li>
                    <li>Review Access Controls</li>
                </ol>
                <p><strong>Important Tips:</strong></p>
                <ul>
                    <li>Pilot the setup with a small group/li>
                    <li>Clearly document rating scales</li>
                    <li>Refine configurations after each cycle</li>
                </ul>
            `,
            position: 'center',
            optional: false
        },

        // FINAL SUMMARY
        {
            element: ['.ps-container'],
            title: 'Performance Management Setup Tutorial Completed!',
            description: 'Congratulations! You’ve completed the Performance Management Setup tutorial.',
            additionalInfo: `
                <p><strong>You’ve learnt how to:</strong></p>
                <ul>
                    <li>Create performance cycles</li>
                    <li>Configure company objectives</li>
                    <li>Define rating methods</li>
                    <li>Add objective measures</li>
                    <li>Set up workflow stages</li>
                    <li>Build peer groups</li>
                    <li>Control access permissions</li>
                </ul>
                <p><strong>Next Steps:</strong></p>
                <ol>
                    <li>Create your first performance cycle.</li>
                    <li>Define your rating methods</li>
                    <li>Configure the workflow stages</li>
                    <li>Assign objectives to employees.</li>
                    <li>Launch with a test group</li>
                </ol>
                <p><strong>Need Help?</strong></p>
                <ul>
                    <li>Click info icons for context help</li>
                    <li>Re-run this tutorial anytime</li>
                    <li>Contact support if needed</li>
                </ul>
            `,
            position: 'center',
            optional: false
        }
    ],
    
    // Additional selectors to make visible in tutorial mode
    additionalSelectors: [
        '.ps-tabs li',
        '.ps-table-no-border',
        '.performance-manage-table',
        '.company-objective-panel',
        '.ps-btn-primary',
        '.ps-btn-warning',
        '.ps-btn-danger',
        '.ps-switch',
        '.ps-dropdown',
        '[data-bind*="visible:"]',
        '[data-visible-feature]',
        '[data-tutorial-element]',
        '.subheading',
        '.page_help'
    ],
    
    // Tab navigation helper
    tabNavigation: {
        'CycleSetup': {
            name: 'Performance Cycles',
            order: 1,
            description: 'Define review periods and timeframes'
        },
        'CompanyObjectives': {
            name: 'Company Objectives',
            order: 2,
            description: 'Set strategic organizational goals'
        },
        'StatusSetup': {
            name: 'Cycle Stages',
            order: 3,
            description: 'Configure workflow steps'
        },
        'RatingSetup': {
            name: 'Rating Methods',
            order: 4,
            description: 'Define evaluation scales'
        },
        'MeasureSetup': {
            name: 'Objective Measures',
            order: 5,
            description: 'Set KPIs and metrics'
        },
        'PeerGroup': {
            name: 'Peer Groups',
            order: 6,
            description: 'Setup 360 feedback groups'
        },
        'AccessControl': {
            name: 'Access Controls',
            order: 7,
            description: 'Manage permissions'
        }
    }
};
    